import type { ReqsReviewConfig, ReqsReviewConfigItem } from '@frontend/api';

type ConfigMap = Record<string, ReqsReviewConfigItem>;
type LabelPathMap = Record<string, string>;

type ConfigMaps = {
  configMap: ConfigMap;
  labelPathMap: LabelPathMap;
};

/**
 * Recursively builds dictionaries of config items and label paths
 * @param item The config item to process
 * @param parentLabels Array of parent labels for building the full label path
 * @returns An object containing both config and label path mappings
 */
const buildPathConfigMaps = (
  item: ReqsReviewConfigItem,
  parentLabels: string[] = [],
): ConfigMaps => {
  const currentLabels = [...parentLabels, item.label];
  const labelPath = currentLabels.join(' / ');

  const configMap: ConfigMap = {
    [item.fullPath]: item,
    // ensure document path is included - will only be specified at the root level
    ...(item.documentPath && item.documentPath !== item.fullPath
      ? { [item.documentPath]: item }
      : {}),
  };

  const labelPathMap: LabelPathMap = {
    [item.fullPath]: labelPath,
    // ensure document path is included with the same label path
    ...(item.documentPath && item.documentPath !== item.fullPath
      ? { [item.documentPath]: labelPath }
      : {}),
  };

  // Process children
  const childMaps = item.children.reduce<ConfigMaps>(
    (acc, child) => {
      const childMaps = buildPathConfigMaps(child, currentLabels);
      return {
        configMap: { ...acc.configMap, ...childMaps.configMap },
        labelPathMap: { ...acc.labelPathMap, ...childMaps.labelPathMap },
      };
    },
    { configMap: {}, labelPathMap: {} },
  );

  return {
    configMap: { ...configMap, ...childMaps.configMap },
    labelPathMap: { ...labelPathMap, ...childMaps.labelPathMap },
  };
};

/**
 * Creates flat dictionaries of config items and label paths keyed by their full query path
 * @param config The requirements review configuration
 * @returns An object containing both the config map and label path map
 */
export const createPathConfigMaps = (config: ReqsReviewConfig): ConfigMaps =>
  config.reduce<ConfigMaps>(
    (acc, item) => {
      const itemMaps = buildPathConfigMaps(item);
      return {
        configMap: { ...acc.configMap, ...itemMaps.configMap },
        labelPathMap: { ...acc.labelPathMap, ...itemMaps.labelPathMap },
      };
    },
    { configMap: {}, labelPathMap: {} },
  );
